/**
 * SmsSocketProvider — one `/api/sms/ws` connection for the whole admin.
 *
 * `useSmsWebSocket` used to open its own socket per call site, and five
 * components mount it: the nav badge, VoiceProvider, the Communication
 * page, Call History and the open-house tab. An admin sitting on the
 * Communication page therefore held three sockets (six under React's
 * StrictMode double-mount in dev) against a backend that caps a user at
 * five. Past the cap the newest connection is refused — which looks
 * exactly like "inbound texts stopped arriving".
 *
 * So the socket lives here, mounted once, and the hook became a
 * subscriber. Every existing call site keeps its old shape.
 */

import {
    createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
    type ReactNode,
} from 'react';
import { API_ROOT_URL } from '@/config/api';
import { TokenManager } from '@/shared/api/tokenManager';
import type { WsEvent } from '@/hooks/useSmsWebSocket';

const RECONNECT_DELAYS = [1000, 2000, 5000, 10000, 30000];
const MAX_RECONNECT_ATTEMPTS = 50;
const PING_INTERVAL_MS = 25000;
const NORMAL_CLOSE_CODE = 1000;
// The backend closes with 4001-4003 for a missing / invalid / non-admin
// token. Retrying those just burns connections.
const AUTH_CLOSE_MIN = 4000;
const AUTH_CLOSE_MAX = 4100;

export type SmsSocketListener = (event: WsEvent) => void;

export interface SmsSocketValue {
    connected: boolean;
    /** Reconnect attempts exhausted — the UI should offer a retry. */
    gaveUp: boolean;
    /** The server rejected our token. Reconnecting cannot fix it. */
    authFailed: boolean;
    reconnect: () => void;
    subscribe: (listener: SmsSocketListener) => () => void;
}

const SmsSocketContext = createContext<SmsSocketValue | null>(null);

const buildSocketUrl = (token: string): string => {
    // Built from the backend root, not the Vite frontend host.
    const apiRoot = new URL(API_ROOT_URL);
    apiRoot.protocol = apiRoot.protocol === 'https:' ? 'wss:' : 'ws:';
    apiRoot.pathname = '/api/sms/ws';
    apiRoot.search = `?token=${encodeURIComponent(token)}`;
    return apiRoot.toString();
};

const logger = (msg: string) => {
    if (import.meta.env.DEV) console.debug(`[WS] ${msg}`);
};

export function SmsSocketProvider({ children }: { children: ReactNode }) {
    const wsRef = useRef<WebSocket | null>(null);
    const listenersRef = useRef(new Set<SmsSocketListener>());
    const reconnectAttempt = useRef(0);
    const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pingTimer = useRef<ReturnType<typeof setInterval> | null>(null);
    const mountedRef = useRef(true);
    const connectRef = useRef<(() => void) | undefined>(undefined);

    const [connected, setConnected] = useState(false);
    const [gaveUp, setGaveUp] = useState(false);
    const [authFailed, setAuthFailed] = useState(false);

    const cleanupTimers = useCallback(() => {
        if (pingTimer.current) { clearInterval(pingTimer.current); pingTimer.current = null; }
        if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }
    }, []);

    connectRef.current = () => {
        if (!mountedRef.current) return;
        // Already open or opening — never stack sockets, that is the bug
        // this provider exists to fix.
        if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) return;

        const token = TokenManager.getToken();
        if (!token) return;

        try {
            const ws = new WebSocket(buildSocketUrl(token));
            wsRef.current = ws;

            ws.onopen = () => {
                if (!mountedRef.current) return;
                setConnected(true);
                setGaveUp(false);
                setAuthFailed(false);
                reconnectAttempt.current = 0;
                pingTimer.current = setInterval(() => {
                    if (ws.readyState === WebSocket.OPEN) ws.send('ping');
                }, PING_INTERVAL_MS);
            };

            ws.onmessage = (event) => {
                let msg: WsEvent;
                try {
                    msg = JSON.parse(event.data);
                } catch {
                    return;  // malformed frame
                }
                // Copied first: a listener may unsubscribe while we fan out.
                for (const listener of Array.from(listenersRef.current)) {
                    try {
                        listener(msg);
                    } catch (err) {
                        logger(`listener threw on ${msg.event}: ${String(err)}`);
                    }
                }
            };

            ws.onclose = (event) => {
                if (wsRef.current === ws) wsRef.current = null;
                if (!mountedRef.current) return;
                setConnected(false);
                cleanupTimers();

                if (event.code >= AUTH_CLOSE_MIN && event.code < AUTH_CLOSE_MAX) {
                    // Retrying with the same rejected token is pointless.
                    setAuthFailed(true);
                    logger('Auth failed, not reconnecting');
                    return;
                }
                if (reconnectAttempt.current >= MAX_RECONNECT_ATTEMPTS) {
                    setGaveUp(true);
                    logger('Max reconnect attempts reached');
                    return;
                }
                const delay = RECONNECT_DELAYS[Math.min(reconnectAttempt.current, RECONNECT_DELAYS.length - 1)];
                reconnectAttempt.current++;
                reconnectTimer.current = setTimeout(() => connectRef.current?.(), delay);
            };

            ws.onerror = () => { /* onclose drives reconnection */ };
        } catch {
            // Constructing the socket failed (bad URL, blocked scheme).
        }
    };

    const reconnect = useCallback(() => {
        reconnectAttempt.current = 0;
        setGaveUp(false);
        setAuthFailed(false);
        connectRef.current?.();
    }, []);

    const subscribe = useCallback((listener: SmsSocketListener) => {
        listenersRef.current.add(listener);
        return () => { listenersRef.current.delete(listener); };
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        connectRef.current?.();
        return () => {
            mountedRef.current = false;
            cleanupTimers();
            wsRef.current?.close(NORMAL_CLOSE_CODE, 'Unmount');
            wsRef.current = null;
            setConnected(false);
        };
    }, [cleanupTimers]);

    const value = useMemo<SmsSocketValue>(
        () => ({ connected, gaveUp, authFailed, reconnect, subscribe }),
        [connected, gaveUp, authFailed, reconnect, subscribe],
    );

    return <SmsSocketContext.Provider value={value}>{children}</SmsSocketContext.Provider>;
}

/**
 * The shared socket, or null when rendered outside the provider — which
 * happens in isolated component tests. Callers degrade to "not
 * connected" rather than crashing.
 */
export function useSmsSocket(): SmsSocketValue | null {
    return useContext(SmsSocketContext);
}
